<template>
  <div
    v-if="filteredCategories.length"
    class="accordion"
    :class="isPlain ? 'accordion--plain' : 'mb-8'"
    :data-test-id="isPlain ? 'product-details-accordion' : undefined"
  >
    <div
      v-for="(section, index) in filteredCategories"
      :key="section.key"
      :class="{ 'mb-2': !isPlain }"
    >
      <!-- The header test id is unconditional so the mobile bars order is
           testable too — ECOM-2773 requires it to match desktop. The bars
           variant only exists below 640px (PdpDescription gates it on
           `isMobile`), so this adds no headers to the desktop DOM. -->
      <button
        :id="`pdp-details-header-${section.key}`"
        class="flex w-full items-center justify-between text-left transition-colors"
        :class="
          isPlain
            ? 'border-b border-solid border-black-10 py-4 hover:border-black px-0'
            : 'bg-darkGold-40 p-4 hover:bg-darkGold-60'
        "
        :aria-expanded="section.isOpen"
        :aria-controls="`pdp-details-panel-${section.key}`"
        :data-test-id="`product-details-accordion-header-${section.key}`"
        @click="toggleCategory(index)"
      >
        <span :class="isPlain ? 'text-lg font-bold' : 'text-lg font-medium'">{{
          section.title
        }}</span>
        <IconArrowDown
          class="size-4 shrink-0 transition-transform duration-150"
          :class="{ '-rotate-180': section.isOpen }"
        />
      </button>
      <transition
        enter-active-class="transition-all duration-150 ease-out"
        leave-active-class="transition-all duration-150 ease-in"
        @enter="onAccordionOpen"
        @leave="onAccordionClose"
      >
        <div
          v-show="section.isOpen"
          :id="`pdp-details-panel-${section.key}`"
          role="region"
          :aria-labelledby="`pdp-details-header-${section.key}`"
          class="accordion-panel overflow-hidden bg-white"
          :class="isPlain ? 'pb-2' : 'px-4'"
          :data-test-id="`product-details-accordion-panel-${section.key}`"
        >
          <component :is="section.component" :product="product" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { markRaw } from 'vue'
import { getFirstAttributeValue, type Product } from '@scayle/storefront-nuxt'
import AccordionDescription from './accordion/AccordionDescription.vue'
import AccordionDetailsExtras from './accordion/AccordionDetailsExtras.vue'
import AccordionCreditorDetails from './accordion/AccordionCreditorDetails.vue'
import AccordionMaterialsCare from '#layers/base/components/product/productDetailsContent/accordion/AccordionMaterialsCare.vue'

const props = defineProps({
  product: {
    type: Object as PropType<Product | null>,
    default: () => null,
  },
  // 'bars' keeps the grey darkGold headers (mobile full-width block, default).
  // 'plain' renders Figma-style divider rows for the desktop in-context column
  // (ECOM-2770). Panel content font size is normalised per variant in the
  // scoped style block below (ECOM-2773), which overrides whatever font-size
  // utility the sub-components set.
  variant: {
    type: String as PropType<'bars' | 'plain'>,
    default: 'bars',
  },
})

const isPlain = computed(() => props.variant === 'plain')

const i18n = useI18n()

const categories = ref([
  {
    key: 'description',
    title: i18n.t('pdp.product_details_description'),
    component: markRaw(AccordionDescription),
    isOpen: false,
    hasContent: !!getProductDescription(props.product),
  },
  {
    key: 'material-care',
    title: i18n.t('pdp.product_details_material_care'),
    component: markRaw(AccordionMaterialsCare),
    isOpen: false,
    hasContent:
      !!getAdvancedAttributeFirstValue(props.product, 'material') ||
      !!getFirstAttributeValue(props.product?.attributes, 'washComment')?.label,
  },
  // TODO: Add size and fit section back in once the data becomes available
  // {
  //   key: 'size-fit',
  //   title: i18n.t('pdp.product_details_size_fit'),
  //   component: AccordionSizeFit,
  //   isOpen: false,
  //   hasContent: false,
  // },
  {
    key: 'details-extras',
    title: i18n.t('pdp.product_details_details_extras'),
    component: markRaw(AccordionDetailsExtras),
    isOpen: false,
    hasContent: !!props.product?.attributes?.articleFeatureIcon?.values,
  },
  {
    key: 'creditor',
    title: i18n.t('pdp.product_creditor_details'),
    component: markRaw(AccordionCreditorDetails),
    isOpen: false,
    hasContent: !!(
      getAdvancedAttributeFirstValue(props.product, 'creditorName') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorAdress') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorPostCode') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorCity') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorCountry') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorWeb')
    ),
  },
])

// Both variants render `categories` in its declared order: Description,
// Material & Care, Details & Extras, Manufacturer. ECOM-2773 requires desktop
// and mobile to match, which replaced the plain variant's separate LEAP order
// (Material & Care first).
const filteredCategories = computed(() =>
  categories.value.filter((category) => category.hasContent),
)

// Default-open the first section that has content — Description, or the next
// available section when it is empty. When no section has content the accordion
// root is not rendered at all (v-if on filteredCategories.length).
const firstOpenSection = filteredCategories.value[0]
if (firstOpenSection) {
  firstOpenSection.isOpen = true
}

const toggleCategory = (index: number) => {
  const category = filteredCategories.value[index]
  if (category) {
    category.isOpen = !category.isOpen
  }
}

const onAccordionOpen = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = '0px'

  // Forces browser reflow; reading offsetHeight has the side-effect, void discards it.
  // eslint-disable-next-line sonarjs/void-use
  void el.offsetHeight

  el.style.height = `${el.scrollHeight}px`

  // We must wait for the transition to end before setting the height to auto
  el.addEventListener(
    'transitionend',
    () => {
      if (el.style.height !== '0px') {
        el.style.height = 'auto'
      }
    },
    { once: true },
  )
}

const onAccordionClose = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = `${el.scrollHeight}px`

  // Forces browser reflow; reading offsetHeight has the side-effect, void discards it.
  // eslint-disable-next-line sonarjs/void-use
  void el.offsetHeight

  el.style.height = '0px'
}
</script>

<style scoped lang="scss">
/*
 * ECOM-2773 normalises panel text, which the content sub-components otherwise
 * render at three different sizes (their own text-sm / text-base / md:text-lg
 * classes, plus `text-md` which is not in our font scale and so resolves to the
 * inherited 16px). Normalising at the panel level keeps that in one place
 * instead of editing every sub-component, matching how MAD already does it.
 *
 * Plain (desktop) is 18px per the Goldner-only part of the ticket; bars
 * (mobile) is 16px, the size the `Material` sub-heading already rendered at.
 */
.accordion--plain :deep(.accordion-panel),
.accordion--plain :deep(.accordion-panel *:not(svg):not(svg *)) {
  font-size: 1.125rem !important;
  line-height: 1.5 !important;
}

.accordion:not(.accordion--plain) :deep(.accordion-panel),
.accordion:not(.accordion--plain)
  :deep(.accordion-panel *:not(svg):not(svg *)) {
  font-size: 1rem !important;
  line-height: 1.5 !important;
}
</style>
